import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Video, Menu, X } from "lucide-react";
import { DOWNLOAD_URL } from "../../lib/data";
import { useLanguage } from "../../lib/i18n";
import { LanguageSelector } from "./LanguageSelector";

export const Nav = () => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const navItems = [
    { label: t("nav.features"), href: "/#features", testid: "features" },
    { label: t("nav.manifesto"), href: "/#manifesto", testid: "manifesto" },
    { label: t("nav.pricing"), href: "/#pricing", testid: "pricing" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="site-nav"
    >
      <div className={`transition-colors duration-500 ${scrolled ? "bg-[#040814]/80 backdrop-blur-xl border-b border-white/5" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
            <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-blue-500 text-white glow-blue">
              <Video size={18} strokeWidth={2.5} />
            </span>
            <span className="font-head font-semibold text-lg tracking-tight">Rexx Calling</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <a key={item.testid} href={item.href} className="text-sm text-slate-400 hover:text-white transition-colors duration-300" data-testid={`nav-link-${item.testid}`}>
                {item.label}
              </a>
            ))}
            <Link to="/download" className="text-sm text-slate-400 hover:text-white transition-colors duration-300" data-testid="nav-link-download">
              {t("nav.download")}
            </Link>
            <LanguageSelector />
            <a
              href={DOWNLOAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-semibold px-5 py-2.5 rounded-full bg-white text-[#040814] hover:bg-blue-500 hover:text-white transition-colors duration-300"
              data-testid="nav-download-btn"
            >
              {t("nav.downloadNow")}
            </a>
          </nav>

          <div className="md:hidden flex items-center gap-3">
            <LanguageSelector />
            <button className="text-white" onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle" aria-label="Toggle menu">
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#040814]/95 backdrop-blur-xl border-b border-white/5"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {navItems.map((item) => (
                <a key={item.testid} href={item.href} onClick={() => setOpen(false)} className="text-slate-300 text-lg">
                  {item.label}
                </a>
              ))}
              <Link to="/download" onClick={() => setOpen(false)} className="text-slate-300 text-lg">
                {t("nav.download")}
              </Link>
              <a href={DOWNLOAD_URL} target="_blank" rel="noopener noreferrer" className="text-center font-semibold px-5 py-3 rounded-full bg-white text-[#040814]">
                {t("nav.downloadNow")}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
